
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { VacationCard } from "./VacationCard";
import { Button } from "@/components/ui/button";
import { Tag, Crown, MapPin, Plane } from "lucide-react";

interface SmartHomepageVacationsProps {
  onShowDeals: () => void;
  onShowLuxury: () => void;
  onShowRondreizen: () => void;
  onShowAllVacations: () => void;
}

export const SmartHomepageVacations = ({ 
  onShowDeals, 
  onShowLuxury, 
  onShowRondreizen, 
  onShowAllVacations 
}: SmartHomepageVacationsProps) => {
  const [deals, setDeals] = useState<any[]>([]);
  const [luxury, setLuxury] = useState<any[]>([]);
  const [rondreizen, setRondreizen] = useState<any[]>([]);
  const [allVacations, setAllVacations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const sortVacationsByPriceAvailability = (vacations: any[]) => {
    return vacations.sort((a, b) => {
      const aHasPrice = a.price !== null && a.price !== undefined && a.price > 0;
      const bHasPrice = b.price !== null && b.price !== undefined && b.price > 0;
      
      // Vacations with known prices come first
      if (aHasPrice && !bHasPrice) return -1;
      if (!aHasPrice && bHasPrice) return 1;
      
      // If both have prices or both don't have prices, maintain original order
      return 0;
    });
  };

  const filterOutRondreizen = (vacations: any[]) => {
    return vacations.filter(vacation => {
      // More specific filtering to avoid filtering out safari luxury vacations
      const isRondreis = 
        (vacation.specialCategories?.toLowerCase().includes('rondreis') && 
         !vacation.specialCategories?.toLowerCase().includes('safari')) ||
        (vacation.categoryPath?.toLowerCase().includes('rondreis') && 
         !vacation.categoryPath?.toLowerCase().includes('safari')) ||
        (vacation.typeVakantie?.toLowerCase().includes('rondreis') && 
         !vacation.typeVakantie?.toLowerCase().includes('safari')) ||
        (vacation.name?.toLowerCase().includes('rondreis') && 
         !vacation.name?.toLowerCase().includes('safari')) ||
        (vacation.description?.toLowerCase().includes('rondreis') && 
         !vacation.description?.toLowerCase().includes('safari'));
      
      // Also exclude if it explicitly mentions "rondrit" or "tour" in a way that suggests it's a touring vacation
      const isTour = 
        vacation.name?.toLowerCase().includes('rondrit') ||
        vacation.description?.toLowerCase().includes('rondrit') ||
        (vacation.name?.toLowerCase().includes('tour') && 
         !vacation.name?.toLowerCase().includes('resort'));
      
      return !isRondreis && !isTour;
    });
  };

  const fetchSmartVacations = async () => {
    setLoading(true);
    
    try {
      // Fetch deals (low price)
      const { data: dealsData } = await supabase
        .from('Complete feed')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .not('price', 'is', null)
        .gt('price', 0)
        .lt('price', 500)
        .order('price', { ascending: true })
        .limit(50);
      
      // Fetch luxury vacations (high price) - exclude rondreizen
      const { data: luxuryDataRaw } = await supabase
        .from('Complete feed')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .not('price', 'is', null)
        .gt('price', 0)
        .gte('price', 2000)
        .order('price', { ascending: false })
        .limit(100);
      
      // Filter out rondreizen from luxury results
      const luxuryData = filterOutRondreizen(luxuryDataRaw || []);
      
      // Fetch rondreizen (using specialCategories or categoryPath)
      const { data: rondreizenData } = await supabase
        .from('Complete feed')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .or('specialCategories.ilike.%rondreis%,categoryPath.ilike.%rondreis%,typeVakantie.ilike.%rondreis%')
        .limit(50);
      
      // Fetch all vacations
      const { data: allVacationsData } = await supabase
        .from('Complete feed')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .limit(50);

      // Apply sorting with price preference and then shuffle
      const sortedDeals = sortVacationsByPriceAvailability(shuffleArray(dealsData || []));
      const sortedLuxury = sortVacationsByPriceAvailability(shuffleArray(luxuryData || []));
      const sortedRondreizen = sortVacationsByPriceAvailability(shuffleArray(rondreizenData || []));
      const sortedAllVacations = sortVacationsByPriceAvailability(shuffleArray(allVacationsData || []));

      setDeals(sortedDeals.slice(0, 3));
      setLuxury(sortedLuxury.slice(0, 3));
      setRondreizen(sortedRondreizen.slice(0, 3));
      setAllVacations(sortedAllVacations.slice(0, 3));

      console.log('Homepage vacations loaded from Complete feed with price preference (luxury excludes rondreizen but keeps safari)');
      
    } catch (error) {
      console.error('Error fetching vacations:', error);
    } finally {
      setLoading(false);
    }
  };

  const shuffleArray = (array: any[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    fetchSmartVacations();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600 mx-auto"></div>
        <p className="mt-2 text-gray-600">Vakanties laden...</p>
      </div>
    );
  }

  const VacationSection = ({ 
    title, 
    vacations, 
    onShowAll, 
    buttonText, 
    icon: Icon,
    bgGradient = "from-orange-500 to-amber-500",
    textColor = "text-orange-600"
  }: {
    title: string;
    vacations: any[];
    onShowAll: () => void;
    buttonText: string;
    icon: any;
    bgGradient?: string;
    textColor?: string;
  }) => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h3 className={`text-3xl font-bold ${textColor} flex items-center gap-3 justify-center font-poppins`}>
          <Icon className="h-8 w-8" />
          {title}
        </h3>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {vacations.map((vacation) => (
          <VacationCard
            key={vacation['product ID']}
            vacation={vacation}
            user={null}
            isFavorite={false}
            onFavoriteChange={() => {}}
          />
        ))}
      </div>
      
      <div className="text-center">
        <Button 
          onClick={onShowAll}
          className={`bg-gradient-to-r ${bgGradient} hover:opacity-90 text-white px-8 py-3 text-lg font-medium rounded-lg shadow-lg transition-all duration-200 hover:shadow-xl transform hover:-translate-y-1`}
        >
          {buttonText}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-16 mt-12">
      <VacationSection
        title="Ontdek de beste deals!"
        vacations={deals}
        onShowAll={onShowDeals}
        buttonText="Bekijk alle deals"
        icon={Tag}
        bgGradient="from-orange-500 to-red-500"
        textColor="text-orange-600"
      />
      
      <VacationSection
        title="Luxe vakanties"
        vacations={luxury}
        onShowAll={onShowLuxury}
        buttonText="Bekijk alle luxe vakanties"
        icon={Crown}
        bgGradient="from-amber-500 to-yellow-500"
        textColor="text-amber-600"
      />
      
      <VacationSection
        title="Bijzondere rondreizen"
        vacations={rondreizen}
        onShowAll={onShowRondreizen}
        buttonText="Bekijk alle rondreizen"
        icon={MapPin}
        bgGradient="from-blue-500 to-indigo-500"
        textColor="text-blue-600"
      />
      
      <VacationSection
        title="Ontdek het complete aanbod"
        vacations={allVacations}
        onShowAll={onShowAllVacations}
        buttonText="Bekijk alle vakanties"
        icon={Plane}
        bgGradient="from-green-500 to-teal-500"
        textColor="text-green-600"
      />
    </div>
  );
};
